import * as React from "react";
import { Controller, useForm } from "react-hook-form";

import { RobotoButton } from "@/shared/components/RobotoButton";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import {
  getCorrespondingGeoType,
  isGeoSpecificType,
  isLatitudeType,
  isLongitudeType,
  MessagePathNode,
} from "@/shared/domain/topics";
import { AddIcon } from "@/shared/foundation/icons/mui";
import {
  actions,
  MapPanelState,
  useVizDispatch,
} from "@/shared/state/visualization";
import { type CoordinateUnit } from "@/shared/state/visualization/schema/v1";

import { DataSourceSelector } from "../../controls/DataSourceSelector";

import styles from "./AddPath.module.css";
import { CoordinateUnitSelect } from "./CoordinateUnitSelect";

interface AddMapPathProps {
  state: MapPanelState;
}

interface AddPathFormData {
  latitude: MessagePathNode | null;
  longitude: MessagePathNode | null;
  latUnit: CoordinateUnit | "";
  lonUnit: CoordinateUnit | "";
}

export function AddPath({ state }: AddMapPathProps) {
  const vizStateDispatch = useVizDispatch();
  const [isAddingNewPath, setIsAddingNewPath] = React.useState(true);

  const form = useForm<AddPathFormData>({
    defaultValues: {
      latitude: null,
      longitude: null,
      latUnit: "",
      lonUnit: "",
    },
    // Validate fields on every change
    mode: "onChange",
  });

  const latitude = form.watch("latitude");
  const longitude = form.watch("longitude");

  // Unit selectors are only shown for generic Number fields — geo-specific types
  // (LatDegFloat, LatDegInt, LonDegFloat, LonDegInt) encode the unit implicitly.
  const needsLatUnit =
    latitude !== null && !isGeoSpecificType(latitude.data.canonical_data_type);
  const needsLonUnit =
    longitude !== null &&
    !isGeoSpecificType(longitude.data.canonical_data_type);

  // Re-trigger unit field validation when the type changes (e.g. switching from
  // Number → geo-specific makes the unit no longer required).
  React.useEffect(() => {
    void form.trigger("latUnit");
  }, [needsLatUnit, form]);

  React.useEffect(() => {
    void form.trigger("lonUnit");
  }, [needsLonUnit, form]);

  const onSubmit = (data: AddPathFormData) => {
    if (data.latitude && data.longitude) {
      // When adding data, we ensure the data is in [lat, lon] pairs
      const pathData = [
        data.latitude.toTopicData(),
        data.longitude.toTopicData(),
      ];
      const coordinateUnits =
        needsLatUnit || needsLonUnit
          ? {
              lat: data.latUnit !== "" ? data.latUnit : undefined,
              lon: data.lonUnit !== "" ? data.lonUnit : undefined,
            }
          : undefined;
      vizStateDispatch(
        actions.addPathToMapPanel(state.id, pathData, coordinateUnits),
      );
      setIsAddingNewPath(false);
      form.reset();
    }
  };

  const handleSubmit = (e: React.BaseSyntheticEvent) => {
    void form.handleSubmit(onSubmit)(e);
  };

  const handleCancel = () => {
    setIsAddingNewPath(false);
    form.reset();
  };

  function isLatitude(messagePathNode: MessagePathNode) {
    if (longitude) {
      return (
        messagePathNode.topic.id === longitude.topic.id &&
        getCorrespondingGeoType(longitude.data.canonical_data_type).includes(
          messagePathNode.data.canonical_data_type,
        )
      );
    }
    return isLatitudeType(messagePathNode.data.canonical_data_type);
  }

  function isLongitude(messagePathNode: MessagePathNode) {
    if (latitude) {
      return (
        messagePathNode.topic.id === latitude.topic.id &&
        getCorrespondingGeoType(latitude.data.canonical_data_type).includes(
          messagePathNode.data.canonical_data_type,
        )
      );
    }
    return isLongitudeType(messagePathNode.data.canonical_data_type);
  }

  function geoSpecificFirst(a: MessagePathNode, b: MessagePathNode) {
    const aIsGeo = isGeoSpecificType(a.data.canonical_data_type);
    const bIsGeo = isGeoSpecificType(b.data.canonical_data_type);
    if (aIsGeo === bIsGeo) {
      return 0;
    }
    return aIsGeo ? -1 : 1;
  }

  if (isAddingNewPath) {
    return (
      <div className={styles.formContainer}>
        <RobotoTypography variant="overline">New path</RobotoTypography>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.fieldGroup}>
            <RobotoTypography component={"label"} variant="subtitle2">
              Latitude
            </RobotoTypography>
            <Controller
              name="latitude"
              control={form.control}
              rules={{ required: true }}
              render={({ field }) => (
                <DataSourceSelector
                  onChange={field.onChange}
                  onClear={() => field.onChange(null)}
                  optionFilter={isLatitude}
                  optionSort={geoSpecificFirst}
                />
              )}
            />
            {needsLatUnit && (
              <Controller
                name="latUnit"
                control={form.control}
                rules={{ required: needsLatUnit }}
                render={({ field }) => (
                  <CoordinateUnitSelect
                    coordinateType="latitude"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                )}
              />
            )}
          </div>

          <div className={styles.fieldGroup}>
            <RobotoTypography component={"label"} variant="subtitle2">
              Longitude
            </RobotoTypography>
            <Controller
              name="longitude"
              control={form.control}
              rules={{ required: true }}
              render={({ field }) => (
                <DataSourceSelector
                  onChange={field.onChange}
                  onClear={() => field.onChange(null)}
                  optionFilter={isLongitude}
                  optionSort={geoSpecificFirst}
                />
              )}
            />
            {needsLonUnit && (
              <Controller
                name="lonUnit"
                control={form.control}
                rules={{ required: needsLonUnit }}
                render={({ field }) => (
                  <CoordinateUnitSelect
                    coordinateType="longitude"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                )}
              />
            )}
          </div>

          <div className={styles.positionRight}>
            <RobotoButton
              eventName={"AddMapPathCanceled"}
              onClick={handleCancel}
              size="small"
              type="button"
            >
              Cancel
            </RobotoButton>
            <RobotoButton
              eventName={"AddMapPathCompleted"}
              type="submit"
              variant="contained"
              size="small"
              disabled={!form.formState.isValid}
            >
              Add Path
            </RobotoButton>
          </div>
        </form>
      </div>
    );
  }

  return (
    <RobotoButton
      eventName={"AddMapPathClicked"}
      onClick={() => setIsAddingNewPath(true)}
      startIcon={<AddIcon />}
      size="small"
    >
      Add Path
    </RobotoButton>
  );
}
